import React, { useContext } from 'react';
import PersonIcon from '@shared/assets/icons/v2/person.svg';
import { MBHighlightedText } from '@shared/components/MBText';
import { MBBoxedIcon, MBImageURISource, MBUserImage } from '@shared/components/image';
import { MBListRow, MBListRowProps } from '@shared/components/list/row';
import { MBTextBody1, MBTextBody1Inactive } from '@shared/components/typography';
import { ColorThemeContext } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { Styles } from './styles';

type User = API.ShortUserFragment & { isRemoved?: boolean | null };

function toString(user: User): string {
  if (user.isPending) {
    return `${user.fullName} (${i18n('menu.pending').toLowerCase()})`;
  }
  if (user.isRemoved) {
    return `${user.fullName} (${i18n('menu.deactivated').toLowerCase()})`;
  }
  return user.fullName;
}

export interface MBUserRowProps extends Omit<MBListRowProps, 'title'> {
  user: User;
  subtitle?: string | React.ReactNode;
  icon?: MBImageURISource | React.ReactNode;
  bold?: boolean;

  /*
        Optional range which creates a highlight in the channel name.
        This is used when displaying channels in search results.
    */
  nameHighlight?: MB.Range;

  imageShadow?: boolean;
  // Selected props passed on from MBListRowProps
  title?: React.ReactNode;
  separator?: boolean;
  isSelected?: boolean;
  onPress?: (node: React.Component) => void;
  isHidingPressableIndicator?: boolean;
}

export function MBUserRow({
  title,
  user,
  imageShadow,
  nameHighlight,
  ...otherProps
}: MBUserRowProps) {
  const colors = useContext(ColorThemeContext);
  return (
    <MBListRow
      title={(() => {
        if (!!title) {
          return title;
        }
        // If we have supplied a range to highlight the name
        // we semi-hackily return a MBHighlightedText instead.
        // The alternative would be to clone this component
        // and have a MBHighlightedUserRow component, but that
        // would have been more maintenance work than this.
        if (!!nameHighlight) {
          return (
            <MBHighlightedText color={colors.foregroundActive} range={nameHighlight}>
              {user.fullName}
            </MBHighlightedText>
          );
        }

        const Type = user.isPending ? MBTextBody1Inactive : MBTextBody1;
        return (
          <Type
            color={
              user.isPending || user.isRemoved ? colors.foregroundInactive : colors.foregroundActive
            }
          >
            {toString(user)}
          </Type>
        );
      })()}
      image={(() => {
        if (user.isPending) {
          return <MBBoxedIcon backgroundColor={colors.foregroundInactive} source={PersonIcon} />;
        }

        return (
          <MBUserImage
            style={[Styles.image, Styles.imageRound, imageShadow && Styles.imageWithShadow]}
            user={user}
          />
        );
      })()}
      {...otherProps}
    />
  );
}
